import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import PropTypes from "prop-types";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import apiClient from "utils/apiClient";
import AdminUserDetail from "./AdminUserDetail";

const TH = ({ children, align }) => (
  <th
    style={{
      padding: "10px 14px",
      textAlign: align || "left",
      fontSize: "0.7rem",
      fontWeight: 700,
      color: "#6b7280",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      borderBottom: "1px solid #f0f0f0",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </th>
);

const TD = ({ children, align }) => (
  <td
    style={{
      padding: "12px 14px",
      textAlign: align || "left",
      borderBottom: "1px solid #f7f7f7",
      fontSize: "0.83rem",
      color: "#374151",
      verticalAlign: "middle",
    }}
  >
    {children}
  </td>
);

TH.propTypes = { children: PropTypes.node.isRequired, align: PropTypes.string };
TH.defaultProps = { align: "left" };
TD.propTypes = { children: PropTypes.node.isRequired, align: PropTypes.string };
TD.defaultProps = { align: "left" };

function formatDate(iso) {
  if (!iso) return <span style={{ color: "#d1d5db" }}>Never</span>;
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function initials(name) {
  return name ? name[0].toUpperCase() : "?";
}

const AVATAR_COLORS = ["#5c6bc0", "#10b981", "#f59e0b", "#ec4899", "#0ea5e9", "#8b5cf6", "#ef4444"];

function avatarColor(id) {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiClient
      .get("/api/admin/users")
      .then((r) => setUsers(r.data))
      .catch(() => setError("Failed to load users"));
  }, []);

  const handleRoleChange = (userId, newRole) => {
    apiClient
      .patch(`/api/admin/users/${userId}`, { role: newRole })
      .then((r) => {
        setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: r.data.role } : u)));
      })
      .catch(() => alert("Failed to update role"));
  };

  const handleDelete = (userId, username) => {
    if (!window.confirm(`Delete user "${username}"? This cannot be undone.`)) return;
    apiClient
      .delete(`/api/admin/users/${userId}`)
      .then(() => setUsers((prev) => prev.filter((u) => u.id !== userId)))
      .catch(() => alert("Failed to delete user"));
  };

  const filtered = users.filter(
    (u) =>
      u.username.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
  );

  if (error)
    return (
      <Typography color="error" variant="body2">
        {error}
      </Typography>
    );

  return (
    <Box>
      <AdminUserDetail
        userId={selectedUserId}
        onClose={() => setSelectedUserId(null)}
        onDeleted={(id) => {
          setUsers((prev) => prev.filter((u) => u.id !== id));
          setSelectedUserId(null);
        }}
      />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
        <Typography variant="body2" sx={{ color: "#6b7280" }}>
          {users.length} total
        </Typography>
        <TextField
          size="small"
          placeholder="Search users…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            width: 220,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontSize: "0.83rem",
              bgcolor: "#fff",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: "1rem", color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <TH>User</TH>
              <TH>Email</TH>
              <TH>Role</TH>
              <TH>Plan</TH>
              <TH>Level</TH>
              <TH>Streak</TH>
              <TH>Joined</TH>
              <TH>Last Login</TH>
              <TH align="center">Actions</TH>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr
                key={u.id}
                style={{ transition: "background 0.1s", cursor: "pointer" }}
                onClick={() => setSelectedUserId(u.id)}
              >
                <TD>
                  <Box display="flex" alignItems="center" gap={1.25}>
                    <Avatar
                      sx={{
                        width: 30,
                        height: 30,
                        fontSize: "0.72rem",
                        bgcolor: avatarColor(u.id),
                        fontWeight: 700,
                      }}
                    >
                      {initials(u.username)}
                    </Avatar>
                    <Typography variant="body2" fontWeight={600} sx={{ color: "#111827" }}>
                      {u.username}
                    </Typography>
                  </Box>
                </TD>
                <TD>
                  <Typography variant="body2" sx={{ color: "#6b7280" }}>
                    {u.email}
                  </Typography>
                </TD>
                <TD>
                  <Select
                    value={u.role}
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleRoleChange(u.id, e.target.value)}
                    sx={{
                      fontSize: "0.75rem",
                      borderRadius: "8px",
                      "& .MuiSelect-select": { py: 0.5, px: 1 },
                    }}
                  >
                    <MenuItem value="user" sx={{ fontSize: "0.78rem" }}>
                      user
                    </MenuItem>
                    <MenuItem value="admin" sx={{ fontSize: "0.78rem" }}>
                      admin
                    </MenuItem>
                  </Select>
                </TD>
                <TD>
                  <Chip
                    label={u.subscription || "free"}
                    size="small"
                    sx={{
                      bgcolor: "#f3f4f6",
                      color: "#6b7280",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  />
                </TD>
                <TD>
                  {u.level != null ? (
                    <Chip
                      label={`Lv ${u.level}`}
                      size="small"
                      sx={{
                        bgcolor: "#eef0fb",
                        color: "#5c6bc0",
                        fontWeight: 600,
                        fontSize: "0.7rem",
                      }}
                    />
                  ) : (
                    <span style={{ color: "#d1d5db" }}>—</span>
                  )}
                </TD>
                <TD>
                  {u.streak != null ? (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <span style={{ fontSize: "0.75rem" }}>🔥</span>
                      <Typography variant="body2">{u.streak}</Typography>
                    </Box>
                  ) : (
                    <span style={{ color: "#d1d5db" }}>—</span>
                  )}
                </TD>
                <TD>{formatDate(u.created_at)}</TD>
                <TD>{formatDate(u.last_login)}</TD>
                <TD align="center">
                  <Tooltip title="Delete user">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(u.id, u.username);
                      }}
                      sx={{ color: "#d1d5db", "&:hover": { color: "#ef4444", bgcolor: "#fee2e2" } }}
                    >
                      <DeleteIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                </TD>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}

export default AdminUsers;
